import type { Account } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import type { PreferencesKey } from '@/data/dal/preferencesKey';

import { BucketSelection } from './bucketSelection';
import { useBuckets } from './useBuckets';

interface RestoreGenericStorageProps {
  region?: string;
  prefix?: string;
  S3: { Bucket?: string };
  GCS: { Bucket?: string };
  StorageAccount: {
    AccountName?: string;
    ResourceGroup?: string;
    Container?: string;
  };
}

type BucketSelectionStepProps<T> = StepProps<
  T & { restoreAccount?: Account }
> & {
  preferencesKey:
    | PreferencesKey.RestoreBucket
    | PreferencesKey.RestoreGcsBucket;
};

export const BucketSelectionStep = <T extends RestoreGenericStorageProps>(
  props: BucketSelectionStepProps<T>
) => {
  const { setWizardState: setBucketSelectionState } = props;
  const dal = useDAL();
  const { data: pref } = dal.preferences.getUserPref(props.preferencesKey);
  const chosenBucket = props.wizardState.S3.Bucket || pref?.value?.bucketName;
  const buckets = useBuckets(
    props.wizardState.restoreAccount,
    props.wizardState.region
  );

  useEffect(() => {
    if (chosenBucket && buckets.length) {
      const availableBucket = buckets.find(
        (x) => x.bucketName === chosenBucket
      );
      const S3 = availableBucket
        ? {
            Bucket: availableBucket.bucketName,
          }
        : {};

      setBucketSelectionState((state) => ({
        ...state,
        S3,
      }));
    }
  }, [buckets, chosenBucket, setBucketSelectionState]);

  return (
    <BucketSelection
      {...props.wizardState}
      restoreAccount={props.wizardState.restoreAccount}
      setBucketSelectionState={setBucketSelectionState}
      buckets={buckets}
    />
  );
};
